import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  BookOpen, FileText, ClipboardList, Bell, Video, MessageCircle, 
  FileQuestion, TrendingUp, ArrowLeft, Plus, Eye, Clock, Users,
  CheckCircle, AlertCircle, BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeacherLMS() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_content: 0,
    total_assignments: 0,
    pending_submissions: 0,
    total_announcements: 0,
    total_quizzes: 0,
    upcoming_classes: 0
  });
  const [recentContent, setRecentContent] = useState([]);
  const [recentAssignments, setRecentAssignments] = useState([]);
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [upcomingClasses, setUpcomingClasses] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        navigate('/staff-portal');
        return;
      }

      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch stats
      const [statsRes, contentRes, assignmentRes, submissionsRes, classesRes] = await Promise.all([
        fetch(`${API_URL}/api/lms/stats/teacher`, { headers }).then(r => r.json()).catch(() => ({})),
        fetch(`${API_URL}/api/lms/content`, { headers }).then(r => r.json()).catch(() => []),
        fetch(`${API_URL}/api/lms/assignments`, { headers }).then(r => r.json()).catch(() => []),
        fetch(`${API_URL}/api/lms/submissions?status=pending`, { headers }).then(r => r.json()).catch(() => []),
        fetch(`${API_URL}/api/lms/online-classes?upcoming_only=true`, { headers }).then(r => r.json()).catch(() => [])
      ]);

      setStats(statsRes || {});
      setRecentContent(Array.isArray(contentRes) ? contentRes.slice(0, 5) : []);
      setRecentAssignments(Array.isArray(assignmentRes) ? assignmentRes.slice(0, 5) : []);
      setPendingSubmissions(Array.isArray(submissionsRes) ? submissionsRes.slice(0, 5) : []);
      setUpcomingClasses(Array.isArray(classesRes) ? classesRes.slice(0, 3) : []);

    } catch (error) {
      console.error('Error fetching LMS data:', error);
      toast.error('Failed to load LMS data');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Course Content', value: stats.total_content || 0, icon: BookOpen, color: 'bg-blue-500', path: '/staff-portal/lms/content' },
    { title: 'Assignments', value: stats.total_assignments || 0, icon: ClipboardList, color: 'bg-green-500', path: '/staff-portal/lms/assignments' },
    { title: 'Pending Review', value: stats.pending_submissions || 0, icon: FileText, color: 'bg-orange-500', path: '/staff-portal/lms/submissions' },
    { title: 'Quizzes', value: stats.total_quizzes || 0, icon: FileQuestion, color: 'bg-purple-500', path: '/staff-portal/lms/quizzes' },
    { title: 'Online Classes', value: stats.upcoming_classes || upcomingClasses.length, icon: Video, color: 'bg-red-500', path: '/staff-portal/lms/classes' },
    { title: 'Announcements', value: stats.total_announcements || 0, icon: Bell, color: 'bg-indigo-500', path: '/staff-portal/lms/announcements' },
  ];

  const quickActions = [
    { label: 'Add Content', icon: BookOpen, path: '/staff-portal/lms/content?action=add', color: 'bg-blue-600' },
    { label: 'Create Assignment', icon: ClipboardList, path: '/staff-portal/lms/assignments?action=add', color: 'bg-green-600' },
    { label: 'Create Quiz', icon: FileQuestion, path: '/staff-portal/lms/quizzes?action=add', color: 'bg-purple-600' },
    { label: 'Schedule Class', icon: Video, path: '/staff-portal/lms/classes?action=add', color: 'bg-red-600' },
    { label: 'Post Announcement', icon: Bell, path: '/staff-portal/lms/announcements?action=add', color: 'bg-indigo-600' },
    { label: 'Student Analytics', icon: BarChart3, path: '/staff-portal/lms/analytics', color: 'bg-orange-600' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/staff-portal/dashboard" className="p-2 hover:bg-indigo-600 rounded-lg">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold">Learning Management System</h1>
              <p className="text-indigo-200 text-sm">Manage your courses, assignments & content</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {statCards.map((stat, idx) => (
            <Link key={idx} to={stat.path}>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardContent className="p-4">
                  <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center mb-3`}>
                    <stat.icon className="h-5 w-5 text-white" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-sm text-gray-500">{stat.title}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {quickActions.map((action, idx) => (
                <Link key={idx} to={action.path}>
                  <Button 
                    variant="outline" 
                    className="w-full h-auto py-4 flex flex-col items-center gap-2 hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-colors"
                  >
                    <action.icon className="h-6 w-6" />
                    <span className="text-xs">{action.label}</span>
                  </Button>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Recent Content */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-blue-600" />
                  Recent Content
                </CardTitle>
                <Link to="/staff-portal/lms/content">
                  <Button variant="ghost" size="sm">View All</Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {recentContent.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No content uploaded yet</p>
              ) : (
                <div className="space-y-3">
                  {recentContent.map((content, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded">
                          <FileText className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{content.title}</p>
                          <p className="text-xs text-gray-500">{content.course_name || content.subject_name}</p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">{content.content_type}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pending Submissions */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                  Pending Review
                </CardTitle>
                <Link to="/staff-portal/lms/submissions">
                  <Button variant="ghost" size="sm">View All</Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {pendingSubmissions.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No pending submissions</p>
              ) : (
                <div className="space-y-3">
                  {pendingSubmissions.map((sub, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-orange-100 rounded">
                          <ClipboardList className="h-4 w-4 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{sub.assignment_title || sub.title}</p>
                          <p className="text-xs text-gray-500">{sub.student_name}</p>
                        </div>
                      </div>
                      <Button size="sm" variant="outline">Review</Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Classes */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Video className="h-5 w-5 text-red-600" />
                  Upcoming Classes
                </CardTitle>
                <Link to="/staff-portal/lms/classes">
                  <Button variant="ghost" size="sm">View All</Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {upcomingClasses.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No upcoming classes scheduled</p>
              ) : (
                <div className="space-y-3">
                  {upcomingClasses.map((cls, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-100 rounded">
                          <Video className="h-4 w-4 text-red-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{cls.title}</p>
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {cls.scheduled_date} {cls.scheduled_time}
                          </p>
                        </div>
                      </div>
                      {cls.meeting_link && (
                        <a href={cls.meeting_link} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" className="bg-red-600 hover:bg-red-700">Join</Button>
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Assignments */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-green-600" />
                  Recent Assignments
                </CardTitle>
                <Link to="/staff-portal/lms/assignments">
                  <Button variant="ghost" size="sm">View All</Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {recentAssignments.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No assignments created yet</p>
              ) : (
                <div className="space-y-3">
                  {recentAssignments.map((assignment, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded">
                          <ClipboardList className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{assignment.title}</p>
                          <p className="text-xs text-gray-500">Due: {assignment.due_date}</p>
                        </div>
                      </div>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                        {assignment.total_marks} marks
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
